import dayjs, { type Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface ReportingPreset {
  label: string;
  from: Date;
  to: Date;
}

export type RouteDefaultKind =
  | 'today'
  | 'last30ThroughToday'
  | 'last30EndingYesterday';

function zonedNow(timezoneName: string, now: Date): Dayjs | null {
  const zone = timezoneName.trim();
  if (!zone) return null;
  return dayjs(now).tz(zone);
}

/** Calendar day of a zoned instant, kept as local Y-M-D for toApiDate. */
function calendarDate(zoned: Dayjs): Date {
  return new Date(zoned.year(), zoned.month(), zoned.date());
}

function span(start: Dayjs, end: Dayjs): { from: Date; to: Date } {
  return { from: calendarDate(start), to: calendarDate(end) };
}

/**
 * Reporting presets for one account IANA timezone.
 * Returns null when the timezone is missing so callers do not guess a zone.
 */
export function getDatePresets(
  timezoneName: string,
  now: Date = new Date(),
): ReportingPreset[] | null {
  const accountNow = zonedNow(timezoneName, now);
  if (!accountNow) return null;

  const preset = (label: string, start: Dayjs, end: Dayjs): ReportingPreset => ({
    label,
    ...span(start, end),
  });

  return [
    preset('Today', accountNow, accountNow),
    preset(
      'Yesterday',
      accountNow.subtract(1, 'day'),
      accountNow.subtract(1, 'day'),
    ),
    preset('Last 7 Days', accountNow.subtract(7, 'day'), accountNow),
    preset('Last 14 Days', accountNow.subtract(14, 'day'), accountNow),
    preset('Last 30 Days', accountNow.subtract(30, 'day'), accountNow),
    preset('This Week', accountNow.startOf('week'), accountNow.endOf('week')),
    preset(
      'Last Week',
      accountNow.subtract(1, 'week').startOf('week'),
      accountNow.subtract(1, 'week').endOf('week'),
    ),
    preset('This Month', accountNow.startOf('month'), accountNow.endOf('month')),
    preset(
      'Last Month',
      accountNow.subtract(1, 'month').startOf('month'),
      accountNow.subtract(1, 'month').endOf('month'),
    ),
    preset('This Year', accountNow.startOf('year'), accountNow.endOf('year')),
    preset(
      'Last Year',
      accountNow.subtract(1, 'year').startOf('year'),
      accountNow.subtract(1, 'year').endOf('year'),
    ),
  ];
}

/** Route defaults use the same account-local calendar as the presets. */
export function getRouteDefaultRange(
  timezoneName: string,
  kind: RouteDefaultKind,
  now: Date = new Date(),
): { from: Date; to: Date } | null {
  const accountNow = zonedNow(timezoneName, now);
  if (!accountNow) return null;
  if (kind === 'today') return span(accountNow, accountNow);
  if (kind === 'last30ThroughToday') {
    return span(accountNow.subtract(30, 'day'), accountNow);
  }
  return span(accountNow.subtract(30, 'day'), accountNow.subtract(1, 'day'));
}
